import React from 'react';
import { Box, Typography, Tooltip, Badge } from '@mui/material';
import CollectionsIcon from '@mui/icons-material/Collections';

/**
 * Theme colors from the app's design system
 */
const THEME_COLORS = {
  primary: '#232323',
  primaryLight: '#404040',
  secondary: '#9E9E9E',
  textPrimary: '#505050',
  border: '#D0D0D0',
  borderDashed: '#C0C0C0',
  background: '#FAFAFA',
  badgeBlue: '#2196F3',
};

export type UploadButtonVariant = 'outlined' | 'contained' | 'text';
export type UploadButtonSize = 'small' | 'medium' | 'large';

export interface UploadButtonProps {
  /** Button label text */
  label?: string;
  /** Click handler for upload */
  onClick: () => void;
  /** Click handler for view - if provided, shows view button */
  onViewClick?: () => void;
  /** Number of files uploaded - shows badge and view button when > 0 */
  fileCount?: number;
  /** Button variant - kept for backward compatibility */
  variant?: UploadButtonVariant;
  /** Button size */
  size?: UploadButtonSize;
  /** Whether button is disabled */
  disabled?: boolean;
  /** Custom upload icon (defaults to document upload icon) */
  icon?: React.ReactNode;
  /** Whether to show icon */
  showIcon?: boolean;
  /** Full width button */
  fullWidth?: boolean;
  /** Tooltip text for upload button */
  uploadTooltip?: string;
  /** Tooltip text for view button */
  viewTooltip?: string;
  /** Custom width - overrides size preset (e.g., 200, '100%', '250px') */
  width?: number | string;
  /** Custom height - overrides size preset (e.g., 48, '56px') */
  height?: number | string;
  /** Custom min-width - overrides size preset (e.g., 150, '200px') */
  minWidth?: number | string;
}

/**
 * Custom Document Upload Icon matching the design
 * Document with upload arrow
 */
const DocumentUploadIcon: React.FC<{ size?: number; color?: string }> = ({ 
  size = 24, 
  color = THEME_COLORS.primary 
}) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Document base */}
    <path 
      d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" 
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    {/* Document fold */}
    <path 
      d="M14 2V8H20" 
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {/* Upload arrow - vertical line */}
    <path 
      d="M12 18V12" 
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {/* Upload arrow - chevron */}
    <path 
      d="M9 14L12 11L15 14" 
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * UploadButton Component
 * 
 * A professional upload zone with dashed border and separate view button.
 * Both buttons have matching dashed border style.
 * 
 * @example
 * ```tsx
 * // Basic usage
 * <UploadButton 
 *   label="Upload" 
 *   onClick={() => setModalOpen(true)} 
 * />
 * 
 * // With view functionality (shows when fileCount > 0)
 * <UploadButton 
 *   label="Upload" 
 *   onClick={handleUpload}
 *   onViewClick={handleView}
 *   fileCount={uploadedFiles.length}
 * />
 * ```
 */
const UploadButton: React.FC<UploadButtonProps> = ({
  label = 'Upload',
  onClick,
  onViewClick,
  fileCount = 0,
  size = 'medium',
  disabled = false,
  icon,
  showIcon = true,
  fullWidth = false,
  uploadTooltip = 'Click to upload files',
  viewTooltip = 'View uploaded files',
  width,
  height,
  minWidth,
}) => {
  // Size configurations matching exact measurements
  const sizeConfig = {
    small: {
      height: 44,
      iconSize: 18,
      fontSize: '13px',
      fontWeight: 600,
      viewButtonSize: 40,
      viewIconSize: 16,
      gap: 12,
      px: 20,
      borderRadius: 10,
    },
    medium: {
      height: 52,
      iconSize: 22,
      fontSize: '15px',
      fontWeight: 700,
      viewButtonSize: 48,
      viewIconSize: 18,
      gap: 16,
      px: 28,
      borderRadius: 12,
    },
    large: {
      height: 60,
      iconSize: 26,
      fontSize: '17px',
      fontWeight: 700,
      viewButtonSize: 56,
      viewIconSize: 22,
      gap: 20,
      px: 36,
      borderRadius: 14,
    },
  };

  const config = sizeConfig[size];
  const showViewButton = fileCount > 0 && onViewClick;

  const UploadIcon = icon || (
    <DocumentUploadIcon 
      size={config.iconSize} 
      color={disabled ? THEME_COLORS.secondary : THEME_COLORS.primary} 
    />
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: `${config.gap}px`,
        width: fullWidth ? '100%' : 'auto',
      }}
    >
      {/* Upload Zone - Dashed Border Box (matching screenshot exactly) */}
      <Tooltip title={uploadTooltip} arrow placement="top">
        <Box
          onClick={disabled ? undefined : onClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            height: height ?? config.height,
            px: `${config.px}px`,
            width: width ?? (fullWidth ? '100%' : 'auto'),
            minWidth: minWidth ?? (fullWidth && !showViewButton ? '100%' : 180),
            flex: fullWidth ? 1 : 'none',
            border: `2px dashed ${disabled ? THEME_COLORS.border : THEME_COLORS.borderDashed}`,
            borderRadius: `${config.borderRadius}px`,
            backgroundColor: disabled ? '#F5F5F5' : THEME_COLORS.background,
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            userSelect: 'none',
            '&:hover': disabled ? {} : {
              backgroundColor: '#F0F0F0',
              borderColor: THEME_COLORS.primaryLight,
            },
            '&:active': disabled ? {} : {
              backgroundColor: '#E8E8E8',
              transform: 'scale(0.995)',
            },
          }}
        >
          {showIcon && UploadIcon}
          <Typography
            sx={{
              fontFamily: 'Nunito, sans-serif',
              fontWeight: config.fontWeight,
              fontSize: config.fontSize,
              color: disabled ? THEME_COLORS.secondary : THEME_COLORS.primary,
              letterSpacing: '0.3px',
            }}
          >
            {label}
          </Typography>
        </Box>
      </Tooltip>

      {/* View Button - Modern gallery icon with badge */}
      {showViewButton && (
        <Tooltip title={`${viewTooltip} (${fileCount})`} arrow placement="top">
          <Badge
            badgeContent={fileCount}
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: THEME_COLORS.badgeBlue,
                color: '#FFFFFF',
                fontSize: '11px',
                fontWeight: 700,
                minWidth: 18,
                width: 18,
                height: 18,
                borderRadius: '50%',
                padding: 0,
                top: 0,
                right: 0,
                border: '2px solid #FFFFFF',
                boxShadow: '0 2px 4px rgba(33, 150, 243, 0.3)',
              },
            }}
          >
            <Box
              onClick={disabled ? undefined : onViewClick}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: config.viewButtonSize,
                height: config.viewButtonSize,
                borderRadius: '12px',
                backgroundColor: disabled ? '#F5F5F5' : '#F7F9FC',
                border: '1px solid #E4E9F2',
                cursor: disabled ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': disabled ? {} : {
                  backgroundColor: '#EDF1F7',
                  borderColor: '#C5CEE0',
                  transform: 'scale(1.02)',
                },
                '&:active': disabled ? {} : {
                  backgroundColor: '#E4E9F2',
                  transform: 'scale(0.98)',
                },
              }}
            >
              <CollectionsIcon 
                sx={{ 
                  fontSize: config.viewIconSize + 2,
                  color: disabled ? THEME_COLORS.secondary : '#5B6B8C',
                }} 
              />
            </Box>
          </Badge>
        </Tooltip>
      )}
    </Box>
  );
};

export default UploadButton;
